import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { DepartmentDTO, RequestDTO, RequestMessageDTO } from '@shared/schemas/requests'
import type { CreateRequestInput } from '@shared/schemas/requests'
import { api } from './api'

export function useDepartments() {
  return useQuery({
    queryKey: ['departments'],
    queryFn: () => api<{ departments: DepartmentDTO[] }>('/api/public/departments').then((r) => r.departments),
  })
}

export function useSubmitRequest() {
  return useMutation({
    mutationFn: (input: CreateRequestInput) =>
      api<{ request: RequestDTO }>('/api/public/requests', { method: 'POST', body: JSON.stringify(input) }),
  })
}

export function useTracking(publicId: string) {
  return useQuery({
    queryKey: ['track', publicId],
    queryFn: () => api<{ request: RequestDTO; messages: RequestMessageDTO[] }>(`/api/public/track/${publicId}`),
  })
}

export function useTrackingReply(publicId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: string) =>
      api<{ message: RequestMessageDTO }>(`/api/public/track/${publicId}/reply`, {
        method: 'POST',
        body: JSON.stringify({ body }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['track', publicId] }),
  })
}

export function useRequestsInbox() {
  return useQuery({
    queryKey: ['requests'],
    queryFn: () => api<{ requests: RequestDTO[] }>('/api/requests').then((r) => r.requests),
  })
}

export function useRequestDetail(id: string | null) {
  return useQuery({
    queryKey: ['requests', id],
    queryFn: () => api<{ request: RequestDTO; messages: RequestMessageDTO[] }>(`/api/requests/${id}`),
    enabled: !!id,
  })
}

export function useTriageRequest(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { action: 'accept' | 'reject' | 'needs_info'; rejectionReason?: string; message?: string }) =>
      api<{ request: RequestDTO }>(`/api/requests/${id}/triage`, { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] })
      queryClient.invalidateQueries({ queryKey: ['requests', id] })
    },
  })
}

export function useReplyToRequest(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: string) =>
      api<{ message: RequestMessageDTO }>(`/api/requests/${id}/messages`, {
        method: 'POST',
        body: JSON.stringify({ body }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['requests', id] }),
  })
}
